import { useEffect, useRef } from 'react';
import type { TakShape, GeoPoint } from '@vtak/shared';
import { createMap2D } from '@/map/map-setup';
import { initShapes2D, applyShapeDiff2D, destroyShapes2D } from '@/map/shapes/shape-2d';
import { fetchGuestMissionArea } from './guest-session';

/** How often the guest re-pulls the mission-area outline. */
const REFRESH_MS = 30_000;
/** Padding (px) when framing the mission area. */
const FIT_PADDING = 48;

/** Collect the lat/lng points a shape contributes, for framing the view. */
function shapePoints(shape: TakShape): GeoPoint[] {
  if ('vertices' in shape && Array.isArray(shape.vertices)) return shape.vertices;
  if ('center' in shape && shape.center) return [shape.center];
  return [];
}

/** Frame the map to the mission-area shapes, if any carry geometry. */
function frame(map: google.maps.Map, shapes: TakShape[]): void {
  const bounds = new google.maps.LatLngBounds();
  let has = false;
  for (const shape of shapes) {
    for (const point of shapePoints(shape)) {
      bounds.extend({ lat: point.lat, lng: point.lon });
      has = true;
    }
  }
  if (has) map.fitBounds(bounds, FIT_PADDING);
}

/**
 * Read-only mission-area map for an approved, view-granted guest. Renders only
 * the area outline shapes returned by the guest-scoped endpoint — no drawing
 * tools, no layers, no live tracks. Refreshes the outline on a slow cadence.
 */
export function GuestMap({ token }: { token: string }): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let map: google.maps.Map | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;
    let shownUids: string[] = [];

    async function refresh(target: google.maps.Map): Promise<void> {
      const shapes = await fetchGuestMissionArea(token);
      if (cancelled) return;
      const nextUids = shapes.map((s) => s.uid);
      const removed = shownUids.filter((uid) => !nextUids.includes(uid));
      // `updated` removes-then-adds each shape, so re-pulling the same set is
      // idempotent; `removed` drops any area the leader has since deleted.
      applyShapeDiff2D(target, { added: [], removed, updated: shapes });
      shownUids = nextUids;
      frame(target, shapes);
    }

    void (async () => {
      initShapes2D();
      map = await createMap2D(container);
      if (cancelled) return;
      await refresh(map);
      timer = setInterval(() => {
        if (map) void refresh(map);
      }, REFRESH_MS);
    })();

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      destroyShapes2D();
    };
  }, [token]);

  return <div ref={containerRef} className="h-full w-full" />;
}
